import type { Cell, CellContext, ColumnMeta, Row, Table } from '@tanstack/react-table';
import { ClickAwayListener, Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { AccessControl } from 'features/accessControl';
import { DotsMenuButton } from './DotsMenuButton';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { Tooltip } from 'components';
import { useKeyPress } from 'hooks/useKeyPress';
import { isCellEditable } from '../utils';

type EditableCellTypes<T> = Pick<CellContext<T, unknown>, 'getValue' | 'column'> & {
  row?: Row<T>;
  table?: Table<T>;
  children: React.ReactNode;
  meta?: ColumnMeta<T, unknown>;
  readModeEl?: React.ReactNode;
  error?: boolean;
  isEditable?: boolean;
  validateRow?: boolean;
  isSupplierEditable?: boolean;
  tooltipTitle?: React.ReactNode;
  editableInNewRow?: boolean;
  hideActions?: boolean;
  editorHeight?: string;
  cell?: Cell<T, unknown>;
  onClickAway?: () => void;
  onClick?: () => void;
};

export const EditableCell = <T extends { id: number | string }>({
  getValue,
  row,
  table,
  meta,
  children,
  readModeEl,
  error,
  isEditable,
  validateRow = true,
  isSupplierEditable,
  tooltipTitle,
  editableInNewRow = false,
  hideActions,
  editorHeight = '100%',
  cell,
  onClickAway: onClickAwayProp,
  onClick: onClickProp,
}: EditableCellTypes<T>) => {
  const initialValue = getValue() as string;
  const [value, setValue] = useState<string>(initialValue);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setValue(initialValue);
    setEditing(false);
  }, [initialValue]);

  const handleSetEditing = (toggle: boolean) => {
    if (toggle === false && editing) {
      setEditing(false);
      return;
    }
    if (toggle) {
      if (row?.original.id === -1 && editableInNewRow) {
        setEditing(true);
        return;
      }
      if (row?.original.id !== -1) {
        setEditing(true);
        return;
      }
    }
  };

  useEffect(() => {
    if (isEditable) {
      handleSetEditing(true);
    }
  }, [isEditable]);

  useKeyPress('Escape', () => {
    if (editing) {
      handleSetEditing(false);
    }
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && editing) {
      handleSetEditing(false);
    }
  };
  const isEditableCell = isCellEditable(meta?.editable, cell);
  const onClickAway = () => {
    if (!error && validateRow) {
      onClickAwayProp?.();
      handleSetEditing(false);
    }
  };
  const onClick = () => {
    onClickProp?.();
    handleSetEditing(true);
  };
  if (!isEditableCell) {
    return (
      <Container isEditable={isEditableCell} tooltipTitle={tooltipTitle}>
        {readModeEl ? readModeEl : value}
      </Container>
    );
  }

  const showDotsMenu = isEditableCell && !hideActions && row?.original.id !== -1;

  return isSupplierEditable ? (
    <>
      <Container
        isEditable={isEditableCell}
        tooltipTitle={editing ? '' : tooltipTitle}
        onClick={onClick}
        error={error}
      >
        {editing ? (
          <ClickAwayListener onClickAway={onClickAway}>
            <Editor height={editorHeight}>
              {React.isValidElement(children) &&
              'props' in children &&
              typeof children.props === 'object'
                ? React.cloneElement(children as React.ReactElement, { onKeyDown: handleKeyDown })
                : children}
            </Editor>
          </ClickAwayListener>
        ) : readModeEl ? (
          readModeEl
        ) : (
          <Value>{value}</Value>
        )}
      </Container>
      {showDotsMenu ? (
        <DotsMenuButtonWrapper>
          <DotsMenuButton
            menuOptions={[
              {
                label: 'Edit',
                icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
                onClick: () => {
                  handleSetEditing(true);
                },
              },
              ...(meta?.onCellDelete
                ? [
                    {
                      label: 'Delete',

                      icon: <Trash data-testid="trash_icon" size={16} />,

                      onClick: () => {
                        if (meta?.onCellDelete) {
                          meta.onCellDelete(table?.options.meta, row?.index || 0);
                        }
                      },
                    },
                  ]
                : []),
            ]}
          />
        </DotsMenuButtonWrapper>
      ) : null}
    </>
  ) : (
    <AccessControl
      write
      fallback={
        <Container isEditable={isEditableCell} tooltipTitle={editing ? '' : tooltipTitle}>
          {readModeEl ? readModeEl : value}
        </Container>
      }
    >
      <>
        <Container
          isEditable={isEditableCell}
          tooltipTitle={editing ? '' : tooltipTitle}
          onClick={onClick}
          error={error}
        >
          {editing ? (
            <ClickAwayListener onClickAway={onClickAway}>
              <Editor height={editorHeight}>
                {React.isValidElement(children) &&
                'props' in children &&
                typeof children.props === 'object'
                  ? React.cloneElement(children as React.ReactElement, { onKeyDown: handleKeyDown })
                  : children}
              </Editor>
            </ClickAwayListener>
          ) : readModeEl ? (
            readModeEl
          ) : (
            <Value>{value}</Value>
          )}
        </Container>
        {showDotsMenu ? (
          <DotsMenuButtonWrapper>
            <DotsMenuButton
              menuOptions={[
                {
                  label: 'Edit',
                  icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
                  onClick: () => {
                    handleSetEditing(true);
                  },
                },
                ...(meta?.onCellDelete
                  ? [
                      {
                        label: 'Delete',

                        icon: <Trash data-testid="trash_icon" size={16} />,

                        onClick: () => {
                          if (meta?.onCellDelete) {
                            meta.onCellDelete(table?.options.meta, row?.index || 0);
                          }
                        },
                      },
                    ]
                  : []),
              ]}
            />
          </DotsMenuButtonWrapper>
        ) : null}
      </>
    </AccessControl>
  );
};

const Editor = styled.div<{ height?: string }>`
  width: 100%;
  height: ${({ height = '100%' }) => height};
`;

const Value = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChildrenContainer = styled('div')<{ error?: boolean; isEditable?: boolean }>`
  height: 100%;
  display: flex;
  align-items: center;
  cursor: ${p => (p.isEditable ? 'pointer' : 'auto')};
  .MuiAutocomplete-popper {
    min-width: 300px;
  }
  .MuiAutocomplete-root {
    width: 100%;
  }
  .MuiAutocomplete-endAdornment {
    display: none;
  }
  .MuiInput-root {
    padding-right: 0 !important;
  }
  ${props => (props.error ? 'border: 1px solid red;' : '')}
`;

const Container = ({
  children,
  tooltipTitle,
  error,
  ...rest
}: Omit<React.HTMLProps<HTMLDivElement>, 'as'> & {
  error?: React.ReactNode;
  tooltipTitle?: React.ReactNode;
  isEditable?: boolean;
}) => {
  return tooltipTitle ? (
    <Tooltip title={tooltipTitle}>
      <ChildrenContainer {...rest}>{children}</ChildrenContainer>
    </Tooltip>
  ) : (
    <ChildrenContainer {...rest}>{children}</ChildrenContainer>
  );
};

const DotsMenuButtonWrapper = styled(Box)`
  max-width: 18px;
`;
